import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";

// Mock WhatsApp messages — no backend table yet for WA.
// Email events come from the real /common/email-logs endpoint.
function iso(minsAgo) { return new Date(Date.now() + minsAgo * 60000).toISOString(); }

const MOCK_WHATSAPP = [
  { id: "w-1", channel: "WHATSAPP", candidateName: "Candidate A", candidateId: 200, recipient: "+91 98765 43210", direction: "OUT", body: "Your interview for Senior React Developer is ready. Please record when convenient.", at: iso(-26 * 60), status: "READ", templateKey: "INTERVIEW_INVITE" },
  { id: "w-2", channel: "WHATSAPP", candidateName: "Candidate A", candidateId: 200, recipient: "+91 98765 43210", direction: "IN", body: "Thanks! Can I record it tomorrow morning?", at: iso(-25 * 60), status: "DELIVERED" },
  { id: "w-3", channel: "WHATSAPP", candidateName: "Candidate A", candidateId: 200, recipient: "+91 98765 43210", direction: "OUT", body: "Absolutely — the link is valid for 48 hours.", at: iso(-25 * 60 + 2), status: "READ" },
  { id: "w-4", channel: "WHATSAPP", candidateName: "Candidate A", candidateId: 200, recipient: "+91 98765 43210", direction: "IN", body: "Also — is the team remote or hybrid?", at: iso(-4), status: "DELIVERED" },
  { id: "w-5", channel: "WHATSAPP", candidateName: "Candidate B", candidateId: 201, recipient: "+91 91234 56780", direction: "OUT", body: "Reminder: your interview for Senior React Developer is still pending.", at: iso(-48 * 60), status: "READ", templateKey: "REMINDER" },
  { id: "w-6", channel: "WHATSAPP", candidateName: "Candidate B", candidateId: 201, recipient: "+91 91234 56780", direction: "IN", body: "On it — doing it this evening.", at: iso(-47 * 60), status: "DELIVERED" },
  { id: "w-7", channel: "WHATSAPP", candidateName: "Candidate B", candidateId: 201, recipient: "+91 91234 56780", direction: "OUT", body: "Your score is ready! Log in to see the report.", at: iso(-4 * 60), status: "READ", templateKey: "SCORE_READY" },
  { id: "w-8", channel: "WHATSAPP", candidateName: "Candidate B", candidateId: 201, recipient: "+91 91234 56780", direction: "IN", body: "Thanks, logging in now.", at: iso(-3 * 60), status: "DELIVERED" },
  { id: "w-9", channel: "WHATSAPP", candidateName: "Candidate C", candidateId: 202, recipient: "+91 99887 66554", direction: "OUT", body: "You have been invited to interview for Senior React Developer.", at: iso(-72 * 60), status: "DELIVERED", templateKey: "INTERVIEW_INVITE" },
  { id: "w-10", channel: "WHATSAPP", candidateName: "Candidate C", candidateId: 202, recipient: "+91 99887 66554", direction: "IN", body: "Can I have a few days to prepare?", at: iso(-55), status: "DELIVERED" },
];

const initials = (name) => (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
const avatarColor = (id) => ["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#7B5AC4"][Math.abs(Number(id) || 0) % 6];
const relTime = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
};
const clockTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const dayKey = (iso) => new Date(iso).toDateString();
const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(today.getTime() - 86400000);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

const Communications = () => {
  const navigate = useNavigate();
  const [emailEvents, setEmailEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const threadRef = useRef(null);

  useEffect(() => {
    document.title = "Communications - Ample Hire";
    (async () => {
      try {
        const { data } = await axiosInstance.get("/common/email-logs?size=100");
        if (data?.success) {
          const mapped = (data.items || []).map((e) => ({
            id: `e-${e.id}`, channel: "EMAIL",
            candidateName: e.recipientName || e.recipientEmail,
            candidateId: e.candidateId,
            recipient: e.recipientEmail, direction: "OUT",
            subject: e.subject, body: e.bodyPreview,
            at: e.sentAt || e.createdAt, status: e.status,
            openedAt: e.openedAt, aiAction: e.aiAction, templateKey: e.templateKey,
          }));
          setEmailEvents(mapped);
        }
      } catch (e) { /* empty */ }
      finally { setLoading(false); }
    })();
  }, []);

  const conversations = useMemo(() => {
    const merged = [...emailEvents, ...MOCK_WHATSAPP];
    const grouped = new Map();
    for (const m of merged) {
      const key = m.candidateId != null ? `id:${m.candidateId}` : `name:${m.candidateName || m.recipient}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key, candidateId: m.candidateId, name: m.candidateName || m.recipient,
          emailRecipient: m.channel === "EMAIL" ? m.recipient : null,
          phone: m.channel === "WHATSAPP" ? m.recipient : null,
          messages: [],
        });
      }
      const convo = grouped.get(key);
      if (m.channel === "EMAIL" && !convo.emailRecipient) convo.emailRecipient = m.recipient;
      if (m.channel === "WHATSAPP" && !convo.phone) convo.phone = m.recipient;
      convo.messages.push(m);
    }
    const convos = Array.from(grouped.values());
    convos.forEach((c) => c.messages.sort((a, b) => new Date(a.at) - new Date(b.at)));
    convos.sort((a, b) => {
      const la = new Date(a.messages[a.messages.length - 1]?.at || 0).getTime();
      const lb = new Date(b.messages[b.messages.length - 1]?.at || 0).getTime();
      return lb - la;
    });
    return convos;
  }, [emailEvents]);

  const filteredConvos = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.trim().toLowerCase();
    return conversations.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.emailRecipient || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      c.messages.some((m) => (m.subject || "").toLowerCase().includes(q) || (m.body || "").toLowerCase().includes(q))
    );
  }, [conversations, search]);

  const selected = useMemo(() => filteredConvos.find((c) => c.key === selectedKey), [filteredConvos, selectedKey]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [selectedKey, selected?.messages.length]);

  const channelCounts = useMemo(() => {
    if (!selected) return { email: 0, whatsapp: 0 };
    return {
      email: selected.messages.filter((m) => m.channel === "EMAIL").length,
      whatsapp: selected.messages.filter((m) => m.channel === "WHATSAPP").length,
    };
  }, [selected]);

  const groupedByDay = useMemo(() => {
    if (!selected) return [];
    const groups = [];
    let currentKey = null;
    for (const m of selected.messages) {
      const k = dayKey(m.at);
      if (k !== currentKey) { groups.push({ day: m.at, items: [] }); currentKey = k; }
      groups[groups.length - 1].items.push(m);
    }
    return groups;
  }, [selected]);

  return (
    <div className="app-content page-enter" style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 16px 24px", height: "calc(100vh - 120px)" }}>
      <style>{`
        .cm-bubble { max-width: 78%; padding: 10px 12px; border-radius: 14px; font-size: 13px; line-height: 1.45; border: 1px solid; box-shadow: 0 1px 2px rgba(15,23,42,0.03); }
        .cm-bubble-email { background: #F5F3FF; border-color: #DDD6FE; }
        .cm-bubble-whatsapp { background: #F0FDF4; border-color: #BBF7D0; }
        .cm-bubble-out { margin-left: auto; }
        .cm-bubble-in { margin-right: auto; }
        .cm-bubble-subject { font-size: 12px; font-weight: 700; color: #0F172A; margin-bottom: 4px; }
        .cm-bubble-meta { font-size: 10px; color: #64748B; margin-top: 4px; display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .cm-channel-icon { font-size: 14px; flex-shrink: 0; margin: 2px 8px 0; }
        .cm-day-sep { text-align: center; margin: 12px 0; }
        .cm-day-sep span { font-size: 10px; padding: 3px 10px; background: rgba(148,163,184,0.15); color: #64748B; border-radius: 100px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", margin: 0 }}>Communications</h1>
          <p style={{ fontSize: 12, color: "#64748B", margin: "2px 0 0" }}>{conversations.length} conversations · email + WhatsApp merged</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => navigate("/admin/email-inbox")} style={btnGhost}>✉️ Email inbox</button>
          <button onClick={() => navigate("/admin/whatsapp-inbox")} style={btnGhost}>💬 WhatsApp inbox</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 0, background: "white", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", height: "calc(100% - 56px)" }}>
        <div style={{ borderRight: "1px solid #E2E8F0", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: 10, borderBottom: "1px solid #F1F5F9" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations"
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #E2E8F0", borderRadius: 100, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading && <div style={{ padding: 20, textAlign: "center", color: "#94A3B8", fontSize: 12 }}>Loading…</div>}
            {!loading && filteredConvos.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "#94A3B8", fontSize: 12 }}>No conversations.</div>}
            {!loading && filteredConvos.map((c) => {
              const last = c.messages[c.messages.length - 1];
              const active = c.key === selectedKey;
              const emailCount = c.messages.filter(m => m.channel === "EMAIL").length;
              const waCount = c.messages.filter(m => m.channel === "WHATSAPP").length;
              return (
                <div key={c.key} onClick={() => setSelectedKey(c.key)}
                  style={{
                    padding: "10px 12px", display: "flex", gap: 10, alignItems: "center", cursor: "pointer",
                    background: active ? "#F8FAFC" : "white",
                    borderLeft: active ? "3px solid #7B5AC4" : "3px solid transparent",
                    borderBottom: "1px solid #F1F5F9",
                  }}>
                  <div style={{ width: 38, height: 38, borderRadius: 19, background: avatarColor(c.candidateId), color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                    {initials(c.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                      <div style={{ fontSize: 10, color: "#94A3B8", whiteSpace: "nowrap" }}>{relTime(last.at)}</div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                      <div style={{ fontSize: 11, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        <span style={{ marginRight: 4 }}>{last.channel === "EMAIL" ? "✉️" : "💬"}</span>
                        {last.subject || last.body}
                      </div>
                      <div style={{ display: "flex", gap: 2 }}>
                        {emailCount > 0 && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 100, background: "#F5F3FF", color: "#7C3AED", fontWeight: 600 }}>✉️ {emailCount}</span>}
                        {waCount > 0 && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 100, background: "#F0FDF4", color: "#16A34A", fontWeight: 600 }}>💬 {waCount}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {!selected && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#F8FAFC", color: "#94A3B8", padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Select a conversation</div>
            <div style={{ fontSize: 12, color: "#94A3B8", textAlign: "center", maxWidth: 320 }}>
              Pick a candidate from the left to see every email and WhatsApp exchange in one merged timeline.
            </div>
          </div>
        )}

        {selected && (
          <div style={{ display: "flex", flexDirection: "column", background: "#FAFAFA" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #E2E8F0", background: "white", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 20, background: avatarColor(selected.candidateId), color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14 }}>{initials(selected.name)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{selected.name}</div>
                <div style={{ fontSize: 11, color: "#64748B", display: "flex", gap: 10 }}>
                  {selected.emailRecipient && <span>✉️ {selected.emailRecipient}</span>}
                  {selected.phone && <span>💬 {selected.phone}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, fontSize: 11 }}>
                <span style={{ padding: "3px 8px", borderRadius: 100, background: "#F5F3FF", color: "#7C3AED", fontWeight: 600 }}>✉️ {channelCounts.email}</span>
                <span style={{ padding: "3px 8px", borderRadius: 100, background: "#F0FDF4", color: "#16A34A", fontWeight: 600 }}>💬 {channelCounts.whatsapp}</span>
              </div>
            </div>
            <div ref={threadRef} style={{ flex: 1, overflowY: "auto", padding: "14px 20px", display: "flex", flexDirection: "column" }}>
              {groupedByDay.map((g, gi) => (
                <div key={gi}>
                  <div className="cm-day-sep"><span>{dayLabel(g.day)}</span></div>
                  {g.items.map((m) => <MessageBubble key={m.id} m={m} />)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MessageBubble = ({ m }) => {
  const isEmail = m.channel === "EMAIL";
  const isOut = m.direction !== "IN";
  const bubbleClass = `cm-bubble cm-bubble-${isEmail ? "email" : "whatsapp"} cm-bubble-${isOut ? "out" : "in"}`;
  return (
    <div style={{ display: "flex", flexDirection: isOut ? "row-reverse" : "row", marginBottom: 8 }}>
      <span className="cm-channel-icon">{isEmail ? "✉️" : "💬"}</span>
      <div className={bubbleClass}>
        {isEmail && m.subject && <div className="cm-bubble-subject">{m.subject}</div>}
        <div style={{ color: "#334155", whiteSpace: "pre-wrap" }}>{m.body || "(no body)"}</div>
        <div className="cm-bubble-meta" style={{ justifyContent: isOut ? "flex-end" : "flex-start" }}>
          <span>{clockTime(m.at)}</span>
          {m.status && <span style={{ fontWeight: 600 }}>· {m.status}</span>}
          {m.templateKey && <span style={{ padding: "1px 5px", borderRadius: 100, background: "rgba(99,102,241,0.1)", color: "#7B5AC4", fontWeight: 600, fontSize: 9 }}>{m.templateKey}</span>}
          {m.aiAction && <span style={{ padding: "1px 5px", borderRadius: 100, background: "#F5F3FF", color: "#7C3AED", fontWeight: 600, fontSize: 9 }}>AI · {m.aiAction}</span>}
          {isEmail && m.openedAt && <span style={{ color: "#0284C7", fontWeight: 600 }}>· Opened</span>}
        </div>
      </div>
    </div>
  );
};

const btnGhost = { padding: "6px 14px", borderRadius: 6, background: "white", color: "#475569", border: "1px solid #E2E8F0", fontWeight: 600, fontSize: 12, cursor: "pointer" };

export default Communications;
